<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { BookOpen, ChartNoAxesCombined, Flower2, Moon, Sprout, Sun, UserRound } from '@lucide/vue'
import { session } from './state'
import { isEnglish, t } from './i18n'
import AssistantDrawer from './components/AssistantDrawer.vue'

const route = useRoute()
const focusRoute = computed(() => route.path === '/practice' || route.path.startsWith('/word-family-practice/'))

/* 壳层：App Bar 页标题映射（仅展示，不触碰路由） */
const pageTitles = computed(() => ({ '/': t('today'), '/textbook': t('textbook'), '/insights': t('insights'), '/cases': 'Dativ / Akkusativ', '/grammar': isEnglish.value ? 'Grammar' : '德语语法', '/garden': t('garden'), '/me': t('profile'), '/story': 'AI Stories', '/practice': t('practice'), '/admin': t('admin') }))
const pageTitle = computed(() => pageTitles.value[route.path]
  || (route.path.startsWith('/word-families') ? (isEnglish.value ? 'Word families' : '词族·例句') : '')
  || (route.path.startsWith('/units/') ? 'Unit' : 'Wortgarten'))

/* 壳层：主题机制 —— 默认跟随 prefers-color-scheme，手动偏好持久化 */
const THEME_KEY = 'wortgarten-theme'
const theme = ref('light')
const media = window.matchMedia('(prefers-color-scheme: dark)')
function applyTheme(value) {
  theme.value = value
  document.documentElement.setAttribute('data-theme', value)
}
function toggleTheme() {
  const next = theme.value === 'dark' ? 'light' : 'dark'
  localStorage.setItem(THEME_KEY, next)
  applyTheme(next)
}
applyTheme(localStorage.getItem(THEME_KEY) || (media.matches ? 'dark' : 'light'))
media.addEventListener('change', (event) => {
  if (!localStorage.getItem(THEME_KEY)) applyTheme(event.matches ? 'dark' : 'light')
})
/* 跨视图同步：ProfileView 三态设置直接写 data-theme，这里观察属性变化同步图标状态 */
new MutationObserver(() => {
  const value = document.documentElement.getAttribute('data-theme')
  if (value && value !== theme.value) theme.value = value
}).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
</script>

<template>
  <div v-if="!session.ready" class="splash"><span class="brand-mark">W</span><p>正在打开词汇花园…</p></div>
  <RouterView v-else-if="!session.user || route.path === '/welcome'" />
  <div v-else :class="['app-shell', { 'practice-route': focusRoute }]">
    <!-- 环境背景：奶油 mesh + 漂浮光斑（奶玻璃质感前提） -->
    <div class="ambient-bg" aria-hidden="true"><i class="blob blob-1"></i><i class="blob blob-2"></i><i class="blob blob-3"></i></div>
    <!-- 顶部紧凑 App Bar（重玻璃，practice 路由下隐藏保持沉浸） -->
    <header class="app-bar">
      <span class="brand-mark sm">W</span>
      <h1 class="app-bar-title">{{ pageTitle }}</h1>
      <button class="app-bar-btn" type="button" :aria-label="theme === 'dark' ? '切换到浅色模式' : '切换到深色模式'" @click="toggleTheme">
        <Sun v-if="theme === 'dark'" :size="18" />
        <Moon v-else :size="18" />
      </button>
    </header>
    <main class="page-shell"><RouterView /></main>
    <!-- 全平台底部胶囊 Tab（practice 路由下隐藏，保留原沉浸行为） -->
    <nav class="bottom-nav">
      <RouterLink to="/"><Sprout :size="21" /><span>{{t('today')}}</span></RouterLink>
      <RouterLink to="/textbook"><BookOpen :size="21" /><span>{{t('textbook')}}</span></RouterLink>
      <RouterLink to="/insights"><ChartNoAxesCombined :size="21" /><span>{{t('insights')}}</span></RouterLink>
      <RouterLink to="/garden"><Flower2 :size="21" /><span>{{t('garden')}}</span></RouterLink>
      <RouterLink to="/me"><UserRound :size="21" /><span>{{t('profile')}}</span></RouterLink>
    </nav>
    <AssistantDrawer />
  </div>
</template>
